import { renderEditorFooterLinks } from './editorFooterLinks.js';
import {
  renderInlineTypographyControls,
  syncInlineTypographyRange,
} from './editorInlineTypographyControls.js';
import { ref } from 'lit/directives/ref.js';
import { mountWebProseMirrorInlineEditor as mountVivaProseMirrorInlineEditor } from '../inline/InlineEditor.js';
import { renderEditorGeometryControls } from './editorGeometryControls.js';
import { renderEditorAutoLayoutControls } from './editorAutoLayoutControls.js';
import { type EditorSettingsModalContext, renderEditorSettingsModal } from './editorSettingsModal.js';
export interface EditorPropertiesAdapters { renderTestimonialLayoutControls: (options: any) => unknown }
import { renderTypographyProperties } from './editorTypographyControls.js';

export class WebEditorPropertiesView {
  constructor(private readonly context: Record<string, any>, private readonly adapters: EditorPropertiesAdapters) {}

  renderers() {
    const { renderTestimonialLayoutControls } = this.adapters;
    const {
      activeFrame,
      activePreviewProfile,
      autoLayoutOperations,
      colorValue,
      commitProseMirrorTextEditor,
      applyInlineTextStyle,
      deleteDefinition,
      deleteSection,
      duplicateDefinition,
      duplicateSection,
      editorIconChoices,
      editorIconLabel,
      editorPages,
      editorPreviewProfiles,
      flippedFanSections,
      floatingToolbarPosition,
      formatVersionDate,
      frameInputValue,
      geometryOperations,
      geometryState,
      handleMediaUpload,
      html,
      inlineTextDraft,
      inlineTextGeometry,
      isFanSection,
      isLoadingVersionHistory,
      isSelectedIcon,
      isSelectedMedia,
      isSelectedText,
      landingAnchorSection,
      locale,
      mediaAcceptForKind,
      mediaLabelForKind,
      moveSection,
      moveRepeatDefinition,
      moveStaticPageBuiltInSection,
      opacityPercent,
      optionalNumberValue,
      orderedSections,
      orderedStaticPageBuiltInSections,
      refreshVersionHistory,
      renderMediaPreview,
      resetFrame,
      restoreVersion,
      sectionTitle,
      sectionToolbarPosition,
      selectedDefinition,
      selectedElementId,
      selectedFieldDefinition,
      selectedFieldValue,
      selectedFrame,
      selectedRootSectionDefinition,
      selectedSection,
      selectedStaticPageBuiltInSection,
      selectedTestimonialDefinition,
      selectedTestimonialIndex,
      setInlineTextDraft,
      setInlineTextGeometry,
      setActiveFrame,
      setActivePreviewProfile,
      setShowVersionHistory,
      showVersionHistory,
      toggleFanFlip,
      testimonialCount,
      testimonialToolbarPosition,
      updateField,
      updateFrame,
      updateFrameHref,
      versionHistory,
      versionKindLabel,
    } = this.context;
    const renderColorControl = (label: string, property: 'color' | 'backgroundColor', fallback: string) => {
      const value = colorValue(selectedFrame[property], fallback);
      return html`
        <label class="vp-editor-color-label">
          <span>${label}</span>
          <div class="vp-editor-color-control">
            <input
              type="color"
              .value=${value}
              @input=${(event: InputEvent) => {
                const nextValue = (event.target as HTMLInputElement).value;
                updateFrame(
                  selectedElementId,
                  property === 'color' ? { color: nextValue } : { backgroundColor: nextValue },
                );
              }}
            />
            <input
              type="text"
              .value=${value}
              @change=${(event: Event) => {
                const nextValue = colorValue((event.target as HTMLInputElement).value, value);
                updateFrame(
                  selectedElementId,
                  property === 'color' ? { color: nextValue } : { backgroundColor: nextValue },
                );
              }}
            />
          </div>
        </label>
      `;
    };

    const renderTransformProperties = () => html`
      <section class="vp-editor-panel-section">
        <p class="vp-editor-kicker">Ebene</p>
        <div class="vp-editor-position-grid">
          <label>
            Ebenentiefe
            <input
              type="number"
              .value=${frameInputValue(selectedFrame.zIndex)}
              @input=${(event: InputEvent) =>
                updateFrame(selectedElementId, {
                  zIndex: optionalNumberValue((event.target as HTMLInputElement).value),
                })}
            />
          </label>
        </div>
        <label class="vp-editor-checkbox">
          <input
            type="checkbox"
            .checked=${selectedFrame.hidden === true}
            @change=${(event: Event) =>
              updateFrame(selectedElementId, { hidden: (event.target as HTMLInputElement).checked })}
          />
          Ausblenden
        </label>
        <button type="button" class="vp-editor-icon-button" title="Darstellung zurücksetzen" aria-label="Darstellung zurücksetzen" @click=${() => resetFrame(selectedElementId)}><span class="icon-[material-symbols--restart-alt]"></span></button>
      </section>
    `;

    const renderLayoutProperties = () => html`
      <section class="vp-editor-panel-section">
        <p class="vp-editor-kicker">Darstellung</p>
        <div class="vp-editor-position-grid">
          <label>
            Radius
            <input
              type="number"
              min="0"
              .value=${frameInputValue(selectedFrame.borderRadius)}
              @input=${(event: InputEvent) =>
                updateFrame(selectedElementId, {
                  borderRadius: optionalNumberValue((event.target as HTMLInputElement).value),
                })}
            />
          </label>
          <label>
            Deckkraft
            <input
              type="number"
              min="0"
              max="100"
              .value=${String(opacityPercent(selectedFrame.opacity))}
              @input=${(event: InputEvent) =>
                updateFrame(selectedElementId, {
                  opacity: Math.min(1, Math.max(0, Number((event.target as HTMLInputElement).value) / 100)),
                })}
            />
          </label>
        </div>
      </section>
    `;

    const renderTextAppearanceProperties = () => {
      if (!isSelectedText) return '';
      return html`
        <section class="vp-editor-panel-section">
          ${renderTypographyProperties({
            definition: selectedFieldDefinition,
            elementId: selectedElementId,
            frame: selectedFrame,
            project: this.context.project,
            updateFrame,
            updateSettings: this.context.updateSettings,
          })}
          <div class="vp-editor-text-color-section">
            <span class="vp-editor-field-heading">Farben</span>
            ${renderColorControl('Text', 'color', '#002a56')}
            ${renderColorControl('Fläche / Highlight', 'backgroundColor', '#002a56')}
          </div>
        </section>
      `;
    };

    const renderTestimonialLayoutProperties = () => {
      const isStorySelection =
        selectedElementId === 'section.story' ||
        selectedElementId === 'story.testimonials' ||
        selectedElementId.startsWith('story.testimonial.');
      if (!isStorySelection) return '';
      return renderTestimonialLayoutControls({
        frame: this.context.project.elementFrames['story.testimonials'] ?? { x: 0, y: 0 },
        updateFrame,
      });
    };

    const renderIconProperties = () => {
      if (!selectedFieldDefinition || !isSelectedIcon) return '';
      const current = selectedFieldValue;
      return html`
        <section class="vp-editor-panel-section">
          <p class="vp-editor-kicker">Icon</p>
          ${renderColorControl('Iconfarbe', 'color', '#002a56')}
          <p class="vp-editor-color-label"><span>Icon wählen</span></p>
          <div class="vp-editor-icon-picker" role="group" aria-label="Icon wählen">
            ${editorIconChoices.map(
              (iconClass: string) => html`
                <button
                  type="button"
                  class=${iconClass === current ? 'is-active' : ''}
                  title=${editorIconLabel(iconClass)}
                  @click=${() => updateField(selectedFieldDefinition, iconClass)}
                >
                  <span class=${iconClass}></span>
                </button>
              `,
            )}
          </div>
        </section>
      `;
    };

    const renderMediaProperties = () => {
      if (!selectedFieldDefinition || !isSelectedMedia) return '';
      const activeFit = selectedFrame.objectFit ?? 'cover';
      return html`
        <section class="vp-editor-panel-section">
          <p class="vp-editor-kicker">${mediaLabelForKind(selectedFieldDefinition.kind)}</p>
          <label>
            Upload
            <input
              type="file"
              accept=${mediaAcceptForKind(selectedFieldDefinition.kind)}
              @change=${(event: Event) => handleMediaUpload(selectedFieldDefinition, event)}
            />
          </label>
          ${renderMediaPreview(selectedFieldDefinition)}
          <div class="vp-editor-icon-row" role="group" aria-label="Anpassung">
            ${(['cover', 'contain', 'fill'] as const).map(
              fit => html`
                <button
                  type="button"
                  class=${activeFit === fit ? 'is-active' : ''}
                  @click=${() => updateFrame(selectedElementId, { objectFit: fit })}
                >
                  ${fit}
                </button>
              `,
            )}
          </div>
        </section>
      `;
    };

    const renderSectionToolbar = () => {
      if ((!selectedSection && !selectedStaticPageBuiltInSection) || !sectionToolbarPosition) return '';
      const isStaticBuiltInSelection = Boolean(selectedStaticPageBuiltInSection);
      const toolbarLabel = selectedSection
        ? sectionTitle(selectedSection)
        : (selectedRootSectionDefinition?.label ?? 'Section');
      const staticSectionIndex = selectedStaticPageBuiltInSection
        ? orderedStaticPageBuiltInSections.indexOf(selectedStaticPageBuiltInSection)
        : -1;
      const modularSectionIndex = selectedSection ? orderedSections.indexOf(selectedSection) : -1;
      const canMoveUp = selectedStaticPageBuiltInSection ? staticSectionIndex > 0 : modularSectionIndex > 0;
      const canMoveDown = selectedStaticPageBuiltInSection
        ? staticSectionIndex >= 0 && staticSectionIndex < orderedStaticPageBuiltInSections.length - 1
        : modularSectionIndex >= 0 && modularSectionIndex < orderedSections.length - 1;
      const moveSelected = (direction: -1 | 1) => {
        if (selectedStaticPageBuiltInSection) moveStaticPageBuiltInSection(selectedStaticPageBuiltInSection, direction);
        else if (selectedSection) moveSection(selectedSection, direction);
      };
      return html`
        <div
          class="vp-editor-section-toolbar"
          style=${`left: ${sectionToolbarPosition.left}px; top: ${sectionToolbarPosition.top}px;`}
        >
          <span class="vp-editor-section-toolbar-label">${toolbarLabel}</span>
          ${isFanSection(selectedSection)
            ? html`<button
                type="button"
                class=${flippedFanSections.has(selectedSection) ? 'is-active' : ''}
                title="Karten umdrehen – Testimonials bearbeiten"
                @click=${() => toggleFanFlip(selectedSection)}
              >
                <span class="icon-[material-symbols--flip-camera-android-outline]"></span>
              </button>`
            : ''}
          ${selectedSection && !isStaticBuiltInSelection
            ? html`<button type="button" title="Duplizieren" @click=${() => duplicateSection(selectedSection)}>
                <span class="icon-[material-symbols--content-copy-outline]"></span>
              </button>`
            : ''}
          <button type="button" title="Nach oben verschieben" ?disabled=${!canMoveUp} @click=${() => moveSelected(-1)}>
            <span class="icon-[material-symbols--arrow-upward]"></span>
          </button>
          <button
            type="button"
            title="Nach unten verschieben"
            ?disabled=${!canMoveDown}
            @click=${() => moveSelected(1)}
          >
            <span class="icon-[material-symbols--arrow-downward]"></span>
          </button>
          ${selectedSection && !isStaticBuiltInSelection
            ? html`<button type="button" title="Löschen" @click=${() => deleteSection(selectedSection)}>
                <span class="icon-[material-symbols--delete-outline]"></span>
              </button>`
            : ''}
        </div>
      `;
    };

    const renderTestimonialToolbar = () => {
      if (!selectedTestimonialDefinition || !testimonialToolbarPosition || selectedTestimonialIndex < 0) return '';
      const stopToolbarEvent = (event: Event) => event.stopPropagation();
      return html`
        <div
          class="vp-editor-testimonial-toolbar"
          role="toolbar"
          aria-label=${`Aktionen für Testimonial ${selectedTestimonialIndex + 1}`}
          style=${`left: ${testimonialToolbarPosition.left}px; top: ${testimonialToolbarPosition.top}px;`}
          @pointerdown=${stopToolbarEvent}
          @click=${stopToolbarEvent}
        >
          <span class="vp-editor-testimonial-toolbar-label">Story ${selectedTestimonialIndex + 1}</span>
          <button
            type="button"
            title="Nach oben verschieben"
            aria-label="Testimonial nach oben verschieben"
            ?disabled=${selectedTestimonialIndex === 0}
            @click=${() => moveRepeatDefinition(selectedTestimonialDefinition, -1)}
          >
            <span class="icon-[material-symbols--arrow-upward]" aria-hidden="true"></span>
          </button>
          <button
            type="button"
            title="Nach unten verschieben"
            aria-label="Testimonial nach unten verschieben"
            ?disabled=${selectedTestimonialIndex >= testimonialCount - 1}
            @click=${() => moveRepeatDefinition(selectedTestimonialDefinition, 1)}
          >
            <span class="icon-[material-symbols--arrow-downward]" aria-hidden="true"></span>
          </button>
          <button
            type="button"
            title="Duplizieren"
            aria-label="Testimonial duplizieren"
            @click=${() => duplicateDefinition(selectedTestimonialDefinition)}
          >
            <span class="icon-[material-symbols--content-copy-outline]" aria-hidden="true"></span>
          </button>
          <button
            type="button"
            class="is-danger"
            title="Löschen"
            aria-label="Testimonial löschen"
            @click=${() => deleteDefinition(selectedTestimonialDefinition)}
          >
            <span class="icon-[material-symbols--delete-outline]" aria-hidden="true"></span>
          </button>
        </div>
      `;
    };

    const renderFloatingTextToolbar = () => {
      if ((!isSelectedText && !isSelectedIcon) || !floatingToolbarPosition) return '';
      const activeAlign = selectedFrame.textAlign ?? 'left';
      const isBold = (selectedFrame.fontWeight ?? 0) >= 700;
      const geometryUnlocked = geometryState.isUnlocked(selectedElementId);
      const hasLegacyOffset = Boolean(selectedFrame.x || selectedFrame.y);
      const nudgeGeometry = (event: KeyboardEvent) => {
        if (!geometryUnlocked) return;
        const step = event.shiftKey ? 3 : 1;
        const deltas = {
          ArrowLeft: [-step, 0],
          ArrowRight: [step, 0],
          ArrowUp: [0, -step],
          ArrowDown: [0, step],
        } as const;
        const delta = deltas[event.key as keyof typeof deltas];
        if (!delta) return;
        event.preventDefault();
        geometryOperations.nudgePlacement(selectedElementId, delta[0], delta[1]);
      };
      const stopToolbarEvent = (event: Event) => event.stopPropagation();
      return html`
        <div
          class="vp-editor-floating-toolbar vp-editor-context-toolbar"
          style=${`left: ${floatingToolbarPosition.left}px; top: ${floatingToolbarPosition.top}px;`}
          role="toolbar"
          aria-label="Werkzeuge für das ausgewählte Element"
          @pointerdown=${stopToolbarEvent}
          @click=${stopToolbarEvent}
        >
          ${isSelectedText
            ? html`
                <button
                  type="button"
                  class=${isBold ? 'is-active' : ''}
                  title="Fett"
                  @click=${() => updateFrame(selectedElementId, { fontWeight: isBold ? undefined : 800 })}
                >
                  <span class="icon-[material-symbols--format-bold]"></span>
                </button>
                ${(['left', 'center', 'right'] as const).map(
                  align => html`
                    <button
                      type="button"
                      class=${activeAlign === align ? 'is-active' : ''}
                      title=${align === 'left' ? 'Linksbündig' : align === 'center' ? 'Zentriert' : 'Rechtsbündig'}
                      @click=${() => updateFrame(selectedElementId, { textAlign: align })}
                    >
                      <span
                        class=${align === 'left'
                          ? 'icon-[material-symbols--format-align-left]'
                          : align === 'center'
                            ? 'icon-[material-symbols--format-align-center]'
                            : 'icon-[material-symbols--format-align-right]'}
                      ></span>
                    </button>
                  `,
                )}
              `
            : ''}
          <label title=${isSelectedIcon ? 'Iconfarbe' : 'Textfarbe'}>
            <span class="icon-[material-symbols--format-color-text]"></span>
            <input
              type="color"
              .value=${colorValue(selectedFrame.color, '#002a56')}
              @input=${(event: InputEvent) =>
                updateFrame(selectedElementId, { color: (event.target as HTMLInputElement).value })}
            />
          </label>
          ${isSelectedText
            ? html`
                <label title="Highlight">
                  <span class="icon-[material-symbols--format-color-fill]"></span>
                  <input
                    type="color"
                    .value=${colorValue(selectedFrame.backgroundColor, '#002a56')}
                    @input=${(event: InputEvent) =>
                      updateFrame(selectedElementId, { backgroundColor: (event.target as HTMLInputElement).value })}
                  />
                </label>
              `
            : ''}
          <button
            type="button"
            class=${geometryUnlocked ? 'is-active' : ''}
            title=${geometryUnlocked ? 'Element sperren' : 'Element entsperren'}
            aria-pressed=${String(geometryUnlocked)}
            @click=${() => geometryState.toggleLock(selectedElementId)}
          >
            <span class=${geometryUnlocked ? 'icon-[material-symbols--lock-open-right-outline]' : 'icon-[material-symbols--lock-outline]'}></span>
          </button>
          <button
            type="button"
            title=${hasLegacyOffset ? 'Alten Versatz zuerst auf Raster übernehmen' : 'Auf Raster verschieben'}
            ?disabled=${!geometryUnlocked || hasLegacyOffset}
            @pointerdown=${(event: PointerEvent) => geometryOperations.startMove(event, selectedElementId)}
            @keydown=${nudgeGeometry}
          ><span class="icon-[material-symbols--open-with]"></span></button>
        </div>
      `;
    };

    const renderInlineTextEditor = () => {
      if (
        !inlineTextDraft ||
        !inlineTextGeometry ||
        inlineTextDraft.elementId !== inlineTextGeometry.elementId ||
        !isSelectedText
      )
        return '';
      const stopEditorEvent = (event: Event) => event.stopPropagation();
      const style = [
        `left: ${inlineTextGeometry.left}px;`,
        `top: ${inlineTextGeometry.top}px;`,
        `width: ${inlineTextGeometry.width}px;`,
        `min-height: ${inlineTextGeometry.minHeight}px;`,
        `font-family: ${inlineTextGeometry.fontFamily};`,
        `font-size: ${inlineTextGeometry.fontSize};`,
        `font-weight: ${inlineTextGeometry.fontWeight};`,
        `line-height: ${inlineTextGeometry.lineHeight};`,
        `letter-spacing: ${inlineTextGeometry.letterSpacing};`,
        `color: ${inlineTextGeometry.color};`,
        `-webkit-text-fill-color: ${inlineTextGeometry.color};`,
        `text-align: ${inlineTextGeometry.textAlign};`,
      ].join(' ');

      // The live value intentionally stays in ProseMirror until commit. Mirroring every keystroke
      // into page-level state would rerender all five full iframe previews and interrupt text entry.
      const inlineStyles = inlineTextDraft.styles ?? this.context.project.elementFrames[inlineTextDraft.elementId]?.inlineTextStyles?.[locale] ?? [];
      const toolbarId = `vp-inline-style-${inlineTextDraft.elementId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
      return html`
        <div
          class="vp-editor-inline-textarea vp-editor-inline-prosemirror"
          style=${style}
          @pointerdown=${stopEditorEvent}
          @click=${stopEditorEvent}
          ${ref(element => {
            if (!(element instanceof HTMLElement)) return;
            mountVivaProseMirrorInlineEditor(element, {
              elementId: inlineTextDraft.elementId,
              sessionKey: inlineTextDraft.editorKey,
              value: inlineTextDraft.value,
              styles: inlineStyles,
              multiline: selectedFieldDefinition?.multiline === true,
              toolbarId,
              onCommit: result =>
                commitProseMirrorTextEditor({ elementId: inlineTextDraft.elementId, ...result }),
              onCancel: () => {
                setInlineTextDraft(null);
                setInlineTextGeometry(null);
                window.requestAnimationFrame(() =>
                  document.querySelector<HTMLElement>('.vp-editor-localization-stepper button')?.focus(),
                );
              },
              onSelectionChange: (editor, value, start, end) => {
                const toolbar = document.getElementById(toolbarId) as HTMLFormElement | null;
                if (toolbar) syncInlineTypographyRange(editor, toolbar, value, start, end, inlineStyles);
              },
            });
          })}
        ></div>
        ${renderInlineTypographyControls({
          draft: inlineTextDraft,
          geometry: inlineTextGeometry,
          styles: inlineStyles,
          applyChange: applyInlineTextStyle,
        })}
      `;
    };

    const renderDesignProperties = () => html`
      <section class="vp-editor-panel-section">
        <p class="vp-editor-kicker">Selection</p>
        <h2>${selectedDefinition?.label ?? selectedElementId}</h2>
        ${selectedFieldDefinition
          ? html`
              <label>
                Inhalt
                ${selectedFieldDefinition.kind === 'profession'
                  ? html`<select
                      .value=${selectedFieldValue}
                      @change=${(event: Event) =>
                        updateField(selectedFieldDefinition, (event.target as HTMLSelectElement).value)}
                    >
                      <option value="bakery">Bäckerei</option>
                      <option value="butcher">Metzgerei</option>
                      <option value="fresh-counter">Frischetheke</option>
                      <option value="gastronomy">Gastronomie</option>
                    </select>`
                  : selectedFieldDefinition.kind === 'freshCounterSubtype'
                    ? html`<select
                        .value=${selectedFieldValue}
                        @change=${(event: Event) =>
                          updateField(selectedFieldDefinition, (event.target as HTMLSelectElement).value)}
                      >
                        <option value="general">Allgemein</option>
                        <option value="cheese">Käse</option>
                        <option value="fish">Fisch</option>
                        <option value="meat">Fleisch</option>
                      </select>`
                    : selectedFieldDefinition.multiline
                      ? html`<textarea
                          .value=${selectedFieldValue}
                          @input=${(event: InputEvent) =>
                            updateField(selectedFieldDefinition, (event.target as HTMLTextAreaElement).value)}
                        ></textarea>`
                      : html`<input
                          type=${selectedFieldDefinition.kind === 'url' || isSelectedMedia ? 'url' : 'text'}
                          .value=${selectedFieldValue}
                          @input=${(event: InputEvent) =>
                            updateField(selectedFieldDefinition, (event.target as HTMLInputElement).value)}
                        />`}
              </label>
            `
          : html`<p class="vp-editor-muted">Layout</p>`}
      </section>

      ${selectedElementId?.startsWith('footer.') ? renderEditorFooterLinks({
        links: this.context.project.content[locale].footer.links,
        definitions: this.context.definitions, updateField, move: moveRepeatDefinition,
        duplicate: duplicateDefinition, remove: deleteDefinition,
        add: () => {
          const next = structuredClone(this.context.project);
          for (const language of ['de', 'en', 'tr'] as const) {
            next.content[language].footer.links.push({ label: language === 'de' ? 'Neuer Link' : language === 'en' ? 'New link' : 'Yeni bağlantı', href: '/' });
          }
          this.context.updateProject(next);
        },
      }) : ''}
      ${renderTestimonialLayoutProperties()}
      ${renderEditorAutoLayoutControls({
        definition: selectedDefinition,
        definitions: this.context.definitions,
        elementId: selectedElementId,
        frame: selectedFrame,
        project: this.context.project,
        activeFrame,
        operations: autoLayoutOperations,
      })}
      ${renderEditorGeometryControls({
        definition: selectedDefinition,
        elementId: selectedElementId,
        frame: selectedFrame,
        project: this.context.project,
        activeFrame,
        activePreviewProfile,
        previewProfiles: editorPreviewProfiles,
        selectPreviewProfile: profile => { setActivePreviewProfile(profile.id); setActiveFrame(profile.editorFrame); },
        state: geometryState,
        operations: geometryOperations,
      })}
      ${renderTransformProperties()} ${renderLayoutProperties()}
      ${renderTextAppearanceProperties()}
      ${renderIconProperties()} ${renderMediaProperties()}
    `;

    const renderPrototypeProperties = () => {
      const frameHref = selectedFrame.href ?? '';
      const anchorOptions = Object.entries(landingAnchorSection).map(([anchor, section]) => ({
        value: `#${anchor}`,
        label: sectionTitle(section),
      }));
      return html`
        <section class="vp-editor-panel-section">
          <p class="vp-editor-kicker">Prototype</p>
          <h2>${selectedDefinition?.label ?? selectedElementId}</h2>
          ${selectedFieldDefinition?.kind === 'url'
            ? html`
                <label>
                  Link
                  <input
                    type="url"
                    .value=${selectedFieldValue}
                    @input=${(event: InputEvent) =>
                      updateField(selectedFieldDefinition, (event.target as HTMLInputElement).value)}
                  />
                </label>
              `
            : html`
                <label>
                  Verlinkung
                  <input
                    type="text"
                    placeholder="#abschnitt, /seite oder https://…"
                    .value=${frameHref}
                    @input=${(event: InputEvent) =>
                      updateFrameHref(selectedElementId, (event.target as HTMLInputElement).value)}
                  />
                </label>
                <label>
                  Schnellziel
                  <select
                    .value=${frameHref}
                    @change=${(event: Event) =>
                      updateFrameHref(selectedElementId, (event.target as HTMLSelectElement).value)}
                  >
                    <option value="" ?selected=${!frameHref}>Kein Link</option>
                    <optgroup label="Abschnitt auf dieser Seite">
                      ${anchorOptions.map(
                        option =>
                          html`<option value=${option.value} ?selected=${option.value === frameHref}>
                            ${option.label}
                          </option>`,
                      )}
                    </optgroup>
                    <optgroup label="Seite">
                      ${editorPages.map(
                        (page: { path: string; label: string }) =>
                          html`<option value=${page.path} ?selected=${page.path === frameHref}>${page.label}</option>`,
                      )}
                    </optgroup>
                  </select>
                </label>
                ${frameHref
                  ? html`<button type="button" class="vp-editor-link-clear vp-editor-icon-button" title="Link entfernen" aria-label="Link entfernen" @click=${() => updateFrameHref(selectedElementId, '')}><span class="icon-[material-symbols--link-off]"></span></button>`
                  : ''}
              `}
        </section>
        ${selectedSection
          ? html`
              <section class="vp-editor-panel-section">
                <p class="vp-editor-kicker">Section</p>
                <div class="vp-editor-button-row">
                  <button type="button" class="vp-editor-icon-button" title="Section nach oben" aria-label="Section nach oben verschieben" @click=${() => moveSection(selectedSection, -1)}><span class="icon-[material-symbols--arrow-upward]"></span></button>
                  <button type="button" class="vp-editor-icon-button" title="Section nach unten" aria-label="Section nach unten verschieben" @click=${() => moveSection(selectedSection, 1)}><span class="icon-[material-symbols--arrow-downward]"></span></button>
                </div>
              </section>
            `
          : ''}
      `;
    };

    const renderVersionHistoryPopover = () => {
      if (!showVersionHistory) return '';
      return html`
        <div class="vp-editor-version-popover" role="dialog" aria-label="Versionsverlauf">
          <div class="vp-editor-version-header">
            <div>
              <h2>Versionsverlauf</h2>
              <p>Gespeicherte Drafts und Publishes laden, falls etwas kaputt gegangen ist.</p>
            </div>
            <button type="button" title="Schließen" @click=${() => setShowVersionHistory(false)}>
              <span class="icon-[material-symbols--close]"></span>
            </button>
          </div>
          <div class="vp-editor-version-actions">
            <button type="button" class="vp-editor-icon-button" title="Versionsverlauf aktualisieren" aria-label="Versionsverlauf aktualisieren" ?disabled=${isLoadingVersionHistory} @click=${refreshVersionHistory}>
              <span class="icon-[material-symbols--refresh]"></span>
            </button>
            <span>${versionHistory.length} Versionen</span>
          </div>
          <div class="vp-editor-version-list">
            ${isLoadingVersionHistory
              ? html`<p class="vp-editor-version-empty">Versionsverlauf wird geladen…</p>`
              : versionHistory.length
                ? versionHistory.map(
                    (entry: { id: string; createdAt: string; kind: string; project: { publishedAt?: string } }) => html`
                      <article class="vp-editor-version-item">
                        <div>
                          <strong>${formatVersionDate(entry.createdAt)}</strong>
                          <span
                            >${versionKindLabel(entry.kind)} ·
                            ${entry.project.publishedAt ? 'mit Publish-Zeitpunkt' : 'Arbeitsstand'}</span
                          >
                        </div>
                        <button type="button" @click=${() => restoreVersion(entry)}>Version laden</button>
                      </article>
                    `,
                  )
                : html`<p class="vp-editor-version-empty">
                    Noch keine gespeicherten Versionen. Nach dem nächsten Speichern erscheint hier ein Snapshot.
                  </p>`}
          </div>
        </div>
      `;
    };

    const renderSettingsModal = () => renderEditorSettingsModal(this.context as unknown as EditorSettingsModalContext);
    return {
      renderSectionToolbar,
      renderTestimonialToolbar,
      renderFloatingTextToolbar,
      renderInlineTextEditor,
      renderDesignProperties,
      renderPrototypeProperties,
      renderVersionHistoryPopover,
      renderSettingsModal,
    };
  }
}
